<template>
  <div v-if="isOpen" class="fixed inset-0 top-[-1rem] bg-black bg-opacity-50 flex items-center justify-center z-50 w-full h-full p-8">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative dark:bg-dark-box-bg dark:border dark:border-dark-border dark:text-dark-text max-h-[90vh] overflow-y-auto" ref="modal" :style="modalStyle">
      <!-- <h2 class="text-2xl font-bold mb-4 text-gray-800 dark:text-dark-text">Edytuj zamówienie</h2> -->
      <div 
        class="cursor-move mb-4 pb-2 border-b dark:border-dark-border"
        @mousedown="startDrag"
        @touchstart="startDrag"
      >
        <h2 class="text-2xl font-bold text-gray-800 dark:text-dark-text">Edytuj zamówienie</h2>
      </div>
      <button @click="closeModal" class="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none dark:text-dark-text dark:hover:text-dark-border-hover">
        <span class="text-xl">&times;</span>
      </button>
      <form @submit.prevent="submitForm" class="space-y-4">

        <!-- MPK -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">ID Zlecenia:</label>
          <input
              v-model="localOrder.mpk"
              type="text"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.mpk }"
              @input="validateField('mpk')"
              disabled
          />
          <span v-if="localErrors.mpk" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.mpk }}</span>
        </div>

        <!-- OrderLink -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Połączone Zlecenie:</label>
          <input
              v-model="localOrder.orderlink"
              type="text"
              @input="filterOrderLinks"
              @focus="showOrderLinkPicker"
              @blur="hideOrderLinkSuggestions"
              placeholder="Wpisz numer MPK istniejącego zlecenia"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.orderlink }"
          />
          <span v-if="localErrors.orderlink" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.orderlink }}</span>

          <!-- Lista podpowiedzi orderlink -->
          <ul v-if="showOrderLinkSuggestions && filteredOrderLinks.length" 
              class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border max-h-48 overflow-y-auto shadow-lg z-10">
            <li
                v-for="order in filteredOrderLinks"
                :key="order.id"
                @mousedown.prevent="selectOrderLink(order)"
                class="p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover border-b dark:border-dark-border last:border-b-0"
            >
              <div class="font-semibold text-gray-800 dark:text-dark-text">{{ order.mpk }}</div>
              <div class="text-sm text-gray-600 dark:text-dark-text-secondary mt-1">
                {{ order.companyName }} • {{ formatDate(order.date) }}
              </div>
            </li>
          </ul>
        </div>

        <!-- DATA -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Data:</label>
          <input
              v-model="localOrder.date"
              type="date"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              placeholder="Wybierz datę"
              @change="validateField('date')"
              @click="showDatePicker"
          />
          <span v-if="localErrors.date" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.date }}</span>
        </div>

        <!-- Firma -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Firma:</label>
          <select
              v-model="localOrder.companyId"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.companyId }"
              @change="validateField('companyId')"
              disabled
          >
            <option value="">Wybierz firmę</option>
            <option v-for="company in companies" :key="company.id" :value="company.id">
              {{ company.companyName }}
            </option>
          </select>
          <span v-if="localErrors.companyId" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.companyId }}</span>
        </div>

        <!-- isDirection -->
         <div class="flex flex-row gap-2">
          <div class="flex items-center"> 
            <input
              type="checkbox"
              id="isDirection"
              v-model="localOrder.direction" 
              class="h-4 w-4 text-button-bg border-gray-300 rounded focus:ring-button-bg dark:text-dark-button-bg dark:focus:ring-dark-button-bg"
            />
            <label for="isDirection" class="ml-2 block text-sm font-semibold text-gray-700 dark:text-dark-text">
              Nie Liczyć dojazd?
            </label>
          </div>

          <!-- isReturn -->
          <div class="flex items-center"> 
            <input
              type="checkbox"
              id="isReturn"
              v-model="localOrder.return" 
              class="h-4 w-4 text-button-bg border-gray-300 rounded focus:ring-button-bg dark:text-dark-button-bg dark:focus:ring-dark-button-bg"
            />
            <label for="isReturn" class="ml-2 block text-sm font-semibold text-gray-700 dark:text-dark-text">
              Nie Liczyć powrotu?
            </label>
          </div>
        </div>

        <h3 class="text-lg font-semibold text-gray-800 dark:text-dark-text">Lista pasażerów</h3>

        <!-- Iteracja przez istniejących pasażerów -->
        <div v-for="(passenger, index) in localOrder.passenger" :key="index" class="mt-4 space-y-4 border-b pb-4 mb-4 border-gray-200 dark:border-dark-border">
          <div class="flex justify-between items-center">
            <p class="font-semibold text-gray-700 dark:text-dark-text">{{ passenger.lastName }} {{ passenger.firstName }}</p>

            <!-- Przyciski edytuj i usuń -->
            <div >
              <button v-if="!passenger.isEditing" @click.prevent="toggleEditMode(index)" class="text-blue-500 hover:text-blue-700 dark:text-dark-status-finished dark:hover:text-dark-status-available">
                Edytuj
              </button>
              <button @click.prevent="removePassenger(index)" class="text-red-500 hover:text-red-700 dark:text-dark-status-unavailable dark:hover:text-dark-status-pending ml-2">
                Usuń
              </button>
            </div>
            <!-- <div>
              <button @click.prevent="toggleEditMode(index)" class="text-blue-500 hover:text-blue-700 dark:text-dark-status-finished dark:hover:text-dark-status-available">
                {{ passenger.isEditing ? 'Zapisz' : 'Edytuj' }}
              </button>
              <button @click.prevent="removePassenger(index)" class="text-red-500 hover:text-red-700 dark:text-dark-status-unavailable dark:hover:text-dark-status-pending ml-2">
                Usuń
              </button>
            </div> -->
          </div>

          <!-- Dane pasażera widoczne tylko w trybie edycji -->
          <div v-if="passenger.isEditing" class="space-y-4">
            <!-- Godzina odbioru -->

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Pozycja sortowania:</label>
              <input
                  v-model="passenger.sortOrder"
                  type="number"
                  placeholder="1"
                  class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              />
            </div>

            <div class="flex items-center"> 
              <input
                type="checkbox"
                id="sharedDropoff"
                v-model="passenger.sharedDropoff" 
                class="h-4 w-4 text-button-bg border-gray-300 rounded focus:ring-button-bg dark:text-dark-button-bg dark:focus:ring-dark-button-bg"
              />
              <label for="sharedDropoff" class="ml-2 block text-sm font-semibold text-gray-700 dark:text-dark-text">
                Wspólny adres?
              </label>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Godzina odbioru:</label>
              <input
                  v-model="passenger.pickupTime"
                  type="text"
                  placeholder="HH:MM"
                  maxlength="5"
                  class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
                  @input="formatTimeSecond(passenger)"
              />
            </div>

<!-- Adres Odbioru Opcje -->
<div>
  <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">
    Adres Odbioru:
  </label>
  <div class="space-y-2">
    <!-- Opcja: Adres domowy -->
    <div class="flex items-center">
      <input
        type="radio"
        :id="'pickup-home-' + index"
        v-model="passenger.pickupOption"
        value="home"
        @change="setPickupAddressForPassenger(passenger)"
        class="mr-2"
      />
      <label :for="'pickup-home-' + index" class="text-gray-700 dark:text-dark-text">
        Do pracy (Adres zamieszkania)
      </label>
    </div>

    <!-- Opcja: Adres pracy -->
    <div class="flex items-center">
      <input
        type="radio"
        :id="'pickup-work-' + index"
        v-model="passenger.pickupOption"
        value="work"
        @change="setPickupAddressForPassenger(passenger)"
        class="mr-2"
      />
      <label :for="'pickup-work-' + index" class="text-gray-700 dark:text-dark-text">
        Z pracy (Adres pracy)
      </label>
    </div>
    <input
      v-if="passenger.pickupOption === 'work'"
      v-model="passenger.pickupAddress"
      type="text"
      placeholder="Wpisz adres pracy"
      @input="filterAddressSuggestions(passenger, 'pickup')"
      @focus="showPickupSuggestions = true"
      @blur="hidePickupSuggestions(passenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
    />

    <ul v-if="showPickupSuggestions && passenger.pickupOption === 'work'" 
    class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
      <li
        v-for="address in (Array.isArray(passenger.filteredPickupAddresses) ? passenger.filteredPickupAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectPickupAddress(address, passenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

<!-- Opcja: Ręczne wpisywanie -->
<div class="flex items-center">
  <input
    type="radio"
    :id="'pickup-manual-' + index"
    v-model="passenger.pickupOption"
    value="manual"
    @change="setPickupAddressForPassenger(passenger)"
    class="mr-2"
  />
  <label :for="'pickup-manual-' + index" class="text-gray-700 dark:text-dark-text">
    Ręczne wpisywanie
  </label>
</div>
<input
  v-if="passenger.pickupOption === 'manual'"
  v-model="passenger.pickupAddress"
  type="text"
  placeholder="Wpisz adres odbioru"
  @input="filterAddressSuggestions(passenger, 'pickup')"
  @focus="showPickupSuggestions = true"
  @blur="hidePickupSuggestions(passenger)"
  class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
/>

<p v-if="passenger.pickupAddress" class="mt-2">
  Wybrany Adres: {{ passenger.pickupAddress }}
</p>

<!-- Lista podpowiedzi -->
<ul v-if="showPickupSuggestions && passenger.pickupOption === 'manual'" 
    class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
  <li
    v-for="address in (Array.isArray(passenger.filteredPickupAddresses) ? passenger.filteredPickupAddresses.slice(0, 3) : [])"
    :key="address.combined"
    @click="selectPickupAddress(address, passenger)"
    class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
  >
    {{ address.combined }}
  </li>
</ul>
  </div>
</div>


<!-- Adres Dowozu Opcje -->
<div>
  <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">
    Adres Dowozu:
  </label>
  <div class="space-y-2">
    <!-- Opcja: Adres domowy -->
    <div class="flex items-center">
      <input
        type="radio"
        :id="'dropoff-home-' + index"
        v-model="passenger.dropoffOption"
        value="home"
        @change="setDropoffAddressForPassenger(passenger)"
        class="mr-2"
      />
      <label :for="'dropoff-home-' + index" class="text-gray-700 dark:text-dark-text">
        Z Pracy (Adres Domu)
      </label>
    </div>

    <!-- Opcja: Adres pracy -->
    <div class="flex items-center">
      <input
        type="radio"
        :id="'dropoff-work-' + index"
        v-model="passenger.dropoffOption"
        value="work"
        @change="setDropoffAddressForPassenger(passenger)"
        class="mr-2"
      />
      <label :for="'dropoff-work-' + index" class="text-gray-700 dark:text-dark-text">
        Do Pracy (Adres Pracy)
      </label>
    </div>

    <!-- Pole tekstowe do wpisania adresu pracy -->
    <input
      v-if="passenger.dropoffOption === 'work'"
      v-model="passenger.dropoffAddress"
      type="text"
      placeholder="Wpisz adres pracy"
      @input="filterAddressSuggestions(passenger, 'dropoff')"
      @focus="showDropoffSuggestions = true"
      @blur="hideDropoffSuggestions(passenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
    />

    <!-- Lista podpowiedzi dla opcji 'Do Pracy (Adres Pracy)' -->
    <ul 
      v-if="showDropoffSuggestions && passenger.dropoffOption === 'work'"
      class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border"
    >
      <li
        v-for="address in (Array.isArray(passenger.filteredDropoffAddresses) ? passenger.filteredDropoffAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectDropoffAddress(address, passenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

    <!-- Opcja: Ręczne wpisywanie -->
    <div class="flex items-center">
      <input
        type="radio"
        :id="'dropoff-manual-' + index"
        v-model="passenger.dropoffOption"
        value="manual"
        @change="setDropoffAddressForPassenger(passenger)"
        class="mr-2"
      />
      <label :for="'dropoff-manual-' + index" class="text-gray-700 dark:text-dark-text">
        Ręczne wpisywanie
      </label>
    </div>

    <!-- Pole tekstowe do ręcznego wpisania adresu -->
    <input
      v-if="passenger.dropoffOption === 'manual'"
      v-model="passenger.dropoffAddress"
      type="text"
      placeholder="Wpisz adres dowozu"
      @input="filterAddressSuggestions(passenger, 'dropoff')"
      @focus="showDropoffSuggestions = true"
      @blur="hideDropoffSuggestions(passenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
    />

    <!-- Lista podpowiedzi dla ręcznego wpisywania adresu -->
    <ul 
      v-if="showDropoffSuggestions && passenger.dropoffOption === 'manual'"
      class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border"
    >
      <li
        v-for="address in (Array.isArray(passenger.filteredDropoffAddresses) ? passenger.filteredDropoffAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectDropoffAddress(address, passenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

    <!-- Wybrany adres dla opcji "home" -->
    <p v-if="passenger.dropoffAddress" class="mt-2">
      Wybrany Adres: {{ passenger.dropoffAddress }}
    </p>
  </div>
</div>

              <div class="flex items-center mt-4">
                  <input type="checkbox" id="swap-passenger" v-model="swapEnabled" class="mr-2" />
                  <label for="swap-passenger" class="text-gray-700 dark:text-dark-text">Podmiana (zakończenie na trasie)</label>
              </div>

              <div v-if="swapEnabled" class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Wybierz pasażera do podmiany:</label>
                <input
                    v-model="swappedPassenger.passengerName"
                    @input="filterSwappedPassengers"
                    @click="showSwappedPassengerSuggestions = true"
                    @blur="hideSwappedPassengerSuggestions"
                    type="text"
                    placeholder="Wpisz imię lub nazwisko"
                    class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
                />

                <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Pozycja sortowania:</label>
                <input
                    v-model="swappedPassenger.sortOrder"
                    type="number"
                    placeholder="1"
                    class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
                />

                <div class="flex items-center"> 
                  <input
                    type="checkbox"
                    id="sharedDropoff"
                    v-model="swappedPassenger.sharedDropoff" 
                    class="h-4 w-4 text-button-bg border-gray-300 rounded focus:ring-button-bg dark:text-dark-button-bg dark:focus:ring-dark-button-bg"
                  />
                  <label for="sharedDropoff" class="ml-2 block text-sm font-semibold text-gray-700 dark:text-dark-text">
                    Wspólny adres?
                  </label>
                </div>

                <ul v-if="showSwappedPassengerSuggestions" class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border max-h-32 overflow-y-auto">
                    <li
                        v-for="passenger in filteredSwappedPassengers"
                        :key="passenger.id"
                        @mousedown.prevent="selectSwappedPassenger(passenger, index)"
                        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
                    >
                        {{ passenger.lastName }} {{ passenger.firstName }}
                    </li>
                </ul>

                <div v-if="swappedPassenger.passengerId">
                  <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Godzina odbioru:</label>
                  <input
                      v-model="swappedPassenger.pickupTime"
                      type="text"
                      placeholder="HH:MM"
                      maxlength="5"
                      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
                      @input="formatTimeForSwapped(swappedPassenger)"
                  />
                </div>

                <div v-if="swappedPassenger.passengerId" class="mt-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Adres dowozu dla drugiego pasażera:</label>
                    <input
                        v-model="swappedPassenger.dropoffAddress"
                        @input="filterSwappedDropoffAddresses"
                        @focus="showSwappedDropoffSuggestions = true; filterSwappedDropoffAddresses()"
                        @blur="hideSwappedDropoffSuggestions"
                        type="text"
                        placeholder="Wpisz adres dowozu"
                        class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
                    />
                    <ul v-if="showSwappedDropoffSuggestions" class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border max-h-32 overflow-y-auto">
                      <li
                        v-for="address in filteredSwappedDropoffAddresses.slice(0, 3)"
                        :key="address.combined"
                        @mousedown.prevent="selectSwappedDropoffAddress(address)"
                        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
                      >
                        {{ address.combined }}
                      </li>
                    </ul>
                </div>

                <hr class="my-4">
            </div>

            <button 
              @click.prevent="toggleEditMode(index)" 
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 transition-colors"
            >
              Zapisz zmiany
            </button>

          </div>
        </div>

        <!-- Formularz dodawania nowego pasażera -->
        <div class="mt-4 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Pasażer:</label>
            <input
                v-model="currentPassenger.passengerName"
                @input="filterPassengers"
                @focus="showPassengerSuggestions = true"
                @blur="hidePassengerSuggestions"
                type="text"
                placeholder="Wybierz pasażera"
                class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
                :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.passengerId }"
            />
            <span v-if="localErrors.passengerId" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.passengerId }}</span>

            <ul v-if="showPassengerSuggestions && filteredPassengers.length && currentPassenger.passengerName" class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
              <li
                  v-for="passenger in filteredPassengers"
                  :key="passenger.id"
                  @mousedown.prevent="selectPassenger(passenger)"
                  class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border"
              >
                 {{ passenger.lastName }} {{ passenger.firstName }}
              </li>
            </ul>
          </div>

          <!-- Godzina odbioru -->
          <div v-if="currentPassenger.passengerId">

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Pozycja sortowania:</label>
              <input
                  v-model="currentPassenger.sortOrder"
                  type="number"
                  placeholder="1"
                  class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              />
            </div>

            <div class="flex items-center"> 
              <input
                type="checkbox"
                id="sharedDropoff"
                v-model="currentPassenger.sharedDropoff" 
                class="h-4 w-4 text-button-bg border-gray-300 rounded focus:ring-button-bg dark:text-dark-button-bg dark:focus:ring-dark-button-bg"
              />
              <label for="sharedDropoff" class="ml-2 block text-sm font-semibold text-gray-700 dark:text-dark-text">
                Wspólny adres?
              </label>
            </div>

            <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Godzina odbioru:</label>
            <input
                v-model="currentPassenger.pickupTime"
                type="text"
                placeholder="HH:MM"
                maxlength="5"
                class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
                @input="formatTime(currentPassenger)"
            />
          </div>


          <!-- Adres Odbioru Opcje -->
<div v-if="currentPassenger.passengerId">
  <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">
    Adres Odbioru:
  </label>
  <div class="space-y-2">
    <!-- Opcja: Adres domowy -->
    <div class="flex items-center">
      <input
        type="radio"
        id="pickup-home-new"
        v-model="pickupOption"
        value="home"
        @change="setPickupAddress(currentPassenger)"
        class="mr-2"
      />
      <label for="pickup-home-new" class="text-gray-700 dark:text-dark-text">
        Do pracy (Adres zamieszkania)
      </label>
    </div>

    <!-- Opcja: Adres pracy -->
    <div class="flex items-center">
      <input
        type="radio"
        id="pickup-home-new"
        v-model="pickupOption"
        value="work"
        @change="setPickupAddress(currentPassenger)"
        class="mr-2"
      />
      <label for="pickup-home-new" class="text-gray-700 dark:text-dark-text">
        Z pracy (Adres pracy)
      </label>
    </div>
    <input
      v-if="pickupOption === 'work'"
      v-model="currentPassenger.pickupAddress"
      type="text"
      placeholder="Wpisz adres pracy"
      @input="filterNewAddressSuggestions(currentPassenger, 'pickup')"
      @focus="showPickupSuggestions = true"
      @blur="hidePickupSuggestions(currentPassenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
    />

    <ul v-if="showPickupSuggestions && pickupOption === 'work'" 
    class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
      <li
        v-for="address in (Array.isArray(currentPassenger.filteredPickupAddresses) ? currentPassenger.filteredPickupAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectPickupAddress(address, currentPassenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

<!-- Opcja: Ręczne wpisywanie -->
<div class="flex items-center">
  <input
    type="radio"
    id="pickup-manual-new"
    v-model="pickupOption"
    value="manual"
    @change="setPickupAddress(currentPassenger)"
    class="mr-2"
  />
  <label for="pickup-manual-new" class="text-gray-700 dark:text-dark-text">
    Ręczne wpisywanie
  </label>
</div>
<input
  v-if="pickupOption === 'manual'"
  v-model="currentPassenger.pickupAddress"
  type="text"
  placeholder="Wpisz adres odbioru"
  @input="filterNewAddressSuggestions(currentPassenger, 'pickup')"
  @focus="showPickupSuggestions = true"
  @blur="hidePickupSuggestions(currentPassenger)"
  class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
/>

<p v-if="pickupOption === 'home'" class="mt-2">
  Wybrany Adres: {{ currentPassenger.pickupAddress }}
</p>

<!-- Lista podpowiedzi -->
<ul v-if="showPickupSuggestions && pickupOption === 'manual'" 
    class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
  <li
    v-for="address in (Array.isArray(currentPassenger.filteredPickupAddresses) ? currentPassenger.filteredPickupAddresses.slice(0, 3) : [])"
    :key="address.combined"
    @click="selectPickupAddress(address, currentPassenger)"
    class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
  >
    {{ address.combined }}
  </li>
</ul>
  </div>
</div>


<!-- Adres Dowozu Opcje -->
<div v-if="currentPassenger.passengerId">
  <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">
    Adres Dowozu:
  </label>
  <div class="space-y-2">
    <!-- Opcja: Adres domowy -->
    <div class="flex items-center">
      <input
        type="radio"
        id="dropoff-home-new"
        v-model="dropoffOption"
        value="home"
        @change="setDropoffAddress(currentPassenger)"
        class="mr-2"
      />
      <label for="dropoff-home-new" class="text-gray-700 dark:text-dark-text">
        Z Pracy (Adres Domu)
      </label>
    </div>

    <!-- Opcja: Adres pracy -->
    <div class="flex items-center">
      <input
        type="radio"
        id="dropoff-work-new"
        v-model="dropoffOption"
        value="work"
        @change="setDropoffAddress(currentPassenger)"
        class="mr-2"
      />
      <label for="dropoff-work-new" class="text-gray-700 dark:text-dark-text">
        Do Pracy (Adres Pracy)
      </label>
    </div>

    <!-- Pole tekstowe do wpisania adresu pracy -->
    <input
      v-if="dropoffOption === 'work'"
      v-model="currentPassenger.dropoffAddress"
      type="text"
      placeholder="Wpisz adres pracy"
      @input="filterNewAddressSuggestions(currentPassenger, 'dropoff')"
      @focus="showDropoffSuggestions = true"
      @blur="hideDropoffSuggestions(currentPassenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
    />

    <!-- Lista podpowiedzi dla opcji 'Do Pracy (Adres Pracy)' -->
    <ul 
      v-if="showDropoffSuggestions && dropoffOption === 'work'"
      class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border"
    >
      <li
        v-for="address in (Array.isArray(currentPassenger.filteredDropoffAddresses) ? currentPassenger.filteredDropoffAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectDropoffAddress(address, currentPassenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

    <!-- Opcja: Ręczne wpisywanie -->
    <div class="flex items-center">
      <input
        type="radio"
        id="dropoff-manual-new"
        v-model="dropoffOption"
        value="manual"
        @change="setDropoffAddress(currentPassenger)"
        class="mr-2"
      />
      <label for="dropoff-manual-new" class="text-gray-700 dark:text-dark-text">
        Ręczne wpisywanie
      </label>
    </div>

    <!-- Pole tekstowe do ręcznego wpisania adresu -->
    <input
      v-if="dropoffOption === 'manual'"
      v-model="currentPassenger.dropoffAddress"
      type="text"
      placeholder="Wpisz adres dowozu"
      @input="filterNewAddressSuggestions(currentPassenger, 'dropoff')"
      @focus="showDropoffSuggestions = true"
      @blur="hideDropoffSuggestions(currentPassenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
    />

    <!-- Lista podpowiedzi dla ręcznego wpisywania adresu -->
    <ul 
      v-if="showDropoffSuggestions && dropoffOption === 'manual'"
      class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border"
    >
      <li
        v-for="address in (Array.isArray(currentPassenger.filteredDropoffAddresses) ? currentPassenger.filteredDropoffAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectDropoffAddress(address, currentPassenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

    <!-- Wybrany adres dla opcji "home" -->
    <p v-if="dropoffOption === 'home'" class="mt-2">
      Wybrany Adres: {{ currentPassenger.dropoffAddress }}
    </p>
  </div>
</div>

          <!-- Przycisk dodawania nowego pasażera -->
          <button v-if="currentPassenger.passengerId" @click="addPassenger" type="button" class="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-500 dark:bg-teal-500 dark:hover:bg-teal-600 transition-colors">
            Dodaj pasażera
          </button>
        </div>

        <!-- Kierowca -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Kierowca:</label>
          <input
              v-model="localOrder.driverName"
              @input="filterDrivers"
              @focus="showDriverSuggestions = true"
              @blur="hideDriverSuggestions"
              type="text"
              placeholder="Wyszukaj kierowcę po imieniu lub nazwisku"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.driverId }"
          />
          <span v-if="localErrors.driverId" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.driverId }}</span>

          <ul v-if="showDriverSuggestions && filteredDrivers.length && localOrder.driverName"
              class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
            <li
                v-for="driver in filteredDrivers"
                :key="driver.id"
                @mousedown.prevent="selectDriver(driver)"
                class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border"
            >
               {{ driver.lastName }} {{ driver.firstName }}
            </li>
          </ul>
        </div>

        <!-- Notatki -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Notatki Dla Kierowcy:</label>
          <textarea
              v-model="localOrder.notesDriver"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.notesDriver }"
              @input="validateField('notesDriver')"
          ></textarea>
          <span v-if="localErrors.notesDriver" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.notesDriver }}</span>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Notatki Dla Dypsozytora:</label>
          <textarea
              v-model="localOrder.notesDispatcher"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.notesDispatcher }"
              @input="validateField('notes')"
          ></textarea>
          <span v-if="localErrors.notesDispatcher" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.notesDispatcher }}</span>
        </div>

        <!-- Status zamówienia -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Status zamówienia:</label>
          <select
              v-model="localOrder.orderStatus"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.orderStatus }"
              @change="validateField('orderStatus')"
          >
            <option value="DO_OBSLUGI" class="text-violet-500 dark:text-violet-500">Do Obsługi</option>
            <option value="WYSŁANE" class="text-orange-500 dark:text-orange-500">Wysłane</option>
            <option value="W_TRAKCIE" class="text-yellow-500 dark:text-yellow-500">W trakcie</option>
            <option value="PRZYJĘTE" class="text-blue-500 dark:text-blue-500">Przyjęte</option>
            <option value="ZAKONCZONY" class="text-red-500 dark:text-red-500">Zakończony</option>
            <option value="ANULOWANE" class="text-purple-500 dark:text-purple-500">Anulowane</option>
          </select>
          <span v-if="localErrors.orderStatus" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.orderStatus }}</span>
        </div>

        <!-- Submit -->
        <div class="flex justify-end">
          <button type="submit"
                  class="w-full bg-button-bg text-button-text py-2 rounded-md hover:bg-button-hover focus:outline-none focus:ring focus:ring-indigo-500 dark:bg-dark-button-bg dark:hover:bg-dark-button-hover">
            Zapisz zmiany
          </button>

        </div>

      </form>
    </div>
  </div>
</template>

<style scoped>
/* Pasek przewijania w trybie ciemnym */
.dark .overflow-y-auto::-webkit-scrollbar {
  width: 8px; /* Szerokość paska przewijania */
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #2c7a7a; /* Teal-700 */
  border-radius: 8px; /* Zaokrąglenie suwaka */
  border: 2px solid #1a5d5d; /* Ciemniejszy odcień teal dla krawędzi suwaka */
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
  background-color: #333333; /* Ciemne tło dla śledzenia paska */
  border-radius: 8px; /* Zaokrąglenie tła paska */
}

/* Pasek przewijania w trybie jasnym */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px; /* Szerokość paska przewijania */
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #d1d5db; /* Jasnoszary kolor suwaka (szary) */
  border-radius: 8px; /* Zaokrąglenie suwaka */
  border: 2px solid #9ca3af; /* Ciemniejszy szary dla krawędzi suwaka */
}

.overflow-y-auto::-webkit-scrollbar-track {
  background-color: #f3f4f6; /* Jasnoszare tło dla śledzenia paska */
  border-radius: 8px; /* Zaokrąglenie tła paska */
}

.cursor-move {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.dragging {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>

<script>
import hotelService from '@/services/hotelService';
import stationService from '@/services/stationService';
import passengersService from '@/services/passengersService';
import driverService from '@/services/driversService';
import companiesService from '@/services/companiesService';

export default {
  props: {
    isOpen: Boolean,
    orderToEdit: Object,
    errors: Object,
    orders: {
      type: Array,
      required: true
    },
  },
  data() {
    return {
      localOrder: {
        ...(this.orderToEdit || {}),
        driverId: this.orderToEdit?.driver?.id || null, // Użyj ID kierowcy
        driverName: this.orderToEdit?.driver != null ? `${this.orderToEdit.driver.lastName} ${this.orderToEdit.driver.firstName}` : '',
        passenger: (this.orderToEdit?.passenger || []).map(passenger => {
        return {
          ...passenger,
          isEditing: false,
          pickupTime: passenger.pickupTime || '',
          pickupAddress: passenger.pickupAddress || '',
          dropoffAddress: passenger.dropoffAddress || '',
          city: passenger.city || '',
          homeAddress: '', // Przypisz homeAddress
          workAddress: '', // Przypisz workAddress
          filteredPickupAddresses: [],
          filteredDropoffAddresses: []
        }
      }),
      },
      noDriverOption: { 
          id: null, 
          firstName: 'Kierowcy', 
          lastName: 'Brak', 
          driverStatus: 'N/A' 
      },
      localErrors: { ...this.errors },
      currentPassenger: {
        passengerName: '',
        firstName: '',
        lastName: '',
        passengerId: null,
        pickupTime: '',
        pickupAddress: '',
        pickupOption: '',
        dropoffOption: '',
        dropoffAddress: '',
        city: '',
        homeAddress: '',
        workAddress: '',
        filteredPickupAddresses: [],
        filteredDropoffAddresses: [],
        sortOrder: null,
        sharedDropoff: false
      },
      filteredPassengers: [],
      filteredPickupAddresses: [],
      filteredDropoffAddresses: [],
      showPassengerSuggestions: false,
      isSubmitting: false,
      pickupOption: '',
      dropoffOption: '',
      showPickupSuggestions: false,
      showDropoffSuggestions: false,
      filteredDrivers: [], // Nowa lista na przefiltrowanych kierowców
      showDriverSuggestions: false, // Flaga do pokazywania/ukrywania listy
      swapEnabled: false,
      swapPassengerSelected: false,
      swappedPassenger: {
        passengerName: '',
        passengerId: null,
        pickupAddress: '',
        dropoffAddress: '',
        pickupTime: '',
        swapped: true,
        sortOrder: null,
        sharedDropoff: false
      },
      filteredSwappedPassengers: [],
      showSwappedPassengerSuggestions: false,
      filteredSwappedDropoffAddresses: [],
      showSwappedDropoffSuggestions: false,
      passengers: [],
      stations: [],
      drivers: [],
      companies: [],
      hotels: [],
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      modalPosition: { x: 0, y: 0 },  
      filteredOrderLinks: [],
      showOrderLinkSuggestions: false
    };
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.fetchModalData();
      }
    },
    orderToEdit: {
      handler(newVal) {
        if (newVal) {
          this.localOrder = { ...newVal };
          this.localOrder.companyId = newVal.company ? newVal.company.id : null;
          // this.localOrder.passengerId = newVal.passenger ? newVal.passenger.id : null;
          this.localOrder.driverId = newVal.driver ? newVal.driver.id : null;
        }
      },
      immediate: true,
      deep: true
    },
    'localOrder.orderlink': {
      handler: function(newVal) {
        if (newVal && newVal.length >= 2) {
          clearTimeout(this.orderLinkTimeout);
          this.orderLinkTimeout = setTimeout(() => {
            this.filterOrderLinks();
          }, 300);
        } else {
          this.filteredOrderLinks = [];
          this.showOrderLinkSuggestions = false;
        }
      },
      immediate: false
    }
  },
  async mounted() {
    this.currentPassenger = this.passengers.map(passenger => ({
      ...passenger,
      isEditing: false,
      pickupTime: passenger.pickupTime || '',
      pickupAddress: passenger.pickupAddress || '',
      dropoffAddress: passenger.dropoffAddress || '',
      city: passenger.city || '',
      homeAddress: passenger.homeAddress || '',
      workAddress: passenger.workAddress || '',
      filteredPickupAddresses: [],
      filteredDropoffAddresses: []
    }));
  },
  computed: {
    modalStyle() {
      return {
        transform: `translate(${this.modalPosition.x}px, ${this.modalPosition.y}px)`
      };
    }
  },
  methods: {
    async fetchModalData() {
      try {
        const stationsData = await stationService.getAllStations();
        this.stations = stationsData.data;
      } catch (error) {
        console.error('Nie udało się pobrać stacji:', error);
      }

      try {
        const hotelsData = await hotelService.getAllHotels();
        this.hotels = hotelsData.data;
      } catch (error) {
        console.error('Nie udało się pobrać hoteli:', error);
      }

      try {
        const passengersData = await passengersService.getAllPassengers();
        this.passengers = passengersData.data;
      } catch (error) {
        console.error('Nie udało się pobrać pasażerów:', error);
      }

      try {
        const driversData = await driverService.getAllDrivers();
        this.drivers = driversData.data;
      } catch (error) {
        console.error('Nie udało się pobrać kierowców:', error);
      }

      try {
        const companiesData = await companiesService.getAllCompanies();
        this.companies = companiesData.data;
      } catch (error) {
        console.error('Nie udało się pobrać firm:', error);
      }
    },
    filterOrderLinks() {
      try {
        const query = this.localOrder.orderlink?.trim().toLowerCase() || '';
        
        if (!query) {
          this.filteredOrderLinks = [];
          this.showOrderLinkSuggestions = false;
          return;
        }

        const existingOrders = this.orders || [];
        
        if (!Array.isArray(existingOrders)) {
          console.warn('existingOrders nie jest tablicą:', existingOrders);
          this.filteredOrderLinks = [];
          return;
        }

        this.filteredOrderLinks = existingOrders
          .filter(order => {
            if (!order || !order.mpk) return false;
            return order.mpk.toLowerCase().includes(query) && 
                  order.mpk !== this.localOrder.mpk;
          })
          .map(order => ({
            mpk: order.mpk,
            id: order.id,
            companyName: order.company?.companyName || 'Brak firmy',
            date: order.date
          }))
          .slice(0, 5);

        this.showOrderLinkSuggestions = this.filteredOrderLinks.length > 0;
      } catch (error) {
        console.error('Błąd w filterOrderLinks:', error);
        this.filteredOrderLinks = [];
        this.showOrderLinkSuggestions = false;
      }
    },
    selectOrderLink(order) {
      this.localOrder.orderlink = order.mpk;
      this.showOrderLinkSuggestions = false;
    },
    hideOrderLinkSuggestions() {
      setTimeout(() => {
        this.showOrderLinkSuggestions = false;
      }, 150);
    },
    showOrderLinkPicker() {
      if (this.localOrder.orderlink) {
        this.filterOrderLinks();
      }
      this.showOrderLinkSuggestions = true;
    },
    formatDate(dateString) {
      if (!dateString) return 'Brak daty';
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL');
    },
    showDatePicker(event) {
      event.target.showPicker();
    },
    formatTimeForSwapped(swappedPassenger) {
      let value = swappedPassenger.pickupTime.replace(/\D/g, '');

      if (value.length > 2) {
        value = value.substring(0, 2) + ':' + value.substring(2, 4);
      }
      
      if (value.length > 5) {
        value = value.substring(0, 5);
      }

      swappedPassenger.pickupTime = value;
    },
    formatTimeSecond(passenger) {
      let value = passenger.pickupTime.replace(/\D/g, '');

      if (value.length > 2) {
        value = value.substring(0, 2) + ':' + value.substring(2, 4);
      }
      
      if (value.length > 5) {
        value = value.substring(0, 5);
      }

      passenger.pickupTime = value;
    },
    formatTime(passenger) {
      // Sprawdzenie bezpieczeństwa
      if (!passenger || typeof passenger.pickupTime === 'undefined' || passenger.pickupTime === null) {
        return;
      }
        
      // Zmieniamy 'this.editPassenger' na zmienną 'passenger'
      let value = passenger.pickupTime.replace(/\D/g, '');

      if (value.length > 2) {
        value = value.substring(0, 2) + ':' + value.substring(2, 4);
      }
      
      if (value.length > 5) {
        value = value.substring(0, 5);
      }

      passenger.pickupTime = value;
    },
    // formatTime() {
    // console.log(2);
    //   let value = this.editPassenger.pickupTime.replace(/\D/g, '');

    //   if (value.length > 2) {
    //     value = value.substring(0, 2) + ':' + value.substring(2, 4);
    //   }
      
    //   if (value.length > 5) {
    //     value = value.substring(0, 5);
    //   }

    //   this.editPassenger.pickupTime = value;
    // },
    toggleEditMode(index) {
      const passenger = this.localOrder.passenger[index];

      if (passenger.isEditing) {
        // Jeśli użytkownik kończy edycję, resetuj pola
        this.addSwappedPassenger();


        passenger.dropoffOption = '';
        passenger.pickupOption = '';
        this.swapEnabled = false;
        this.swappedPassenger = {
          passengerName: '',
          passengerId: null,
          pickupAddress: '',
          dropoffAddress: '',
          swapped: false,
          sortOrder: null,
          sharedDropoff: false
        };
        // passenger.pickupTime = '';
      }

      // Przełącz tryb edycji
      passenger.isEditing = !passenger.isEditing;
    },
    addSwappedPassenger() {
        // Sprawdź, czy pasażer na podmianę został wybrany
        if (!this.swappedPassenger.passengerId) {
          console.warn("Nie wybrano pasażera do podmiany.");
          return;
        }

        // Ustaw flagę `isSwap` na true
        this.swappedPassenger.isSwap = false;
        
        // Opcjonalnie: Ustaw `isEditing` na true, aby pola były od razu widoczne
        this.swappedPassenger.isEditing = false;

        // Dodaj skopiowany obiekt pasażera do głównej tablicy pasażerów
        this.localOrder.passenger.push({ ...this.swappedPassenger });

        // Posortuj pasażerów po `sortOrder`
        // this.sortPassengers();

        // Zresetuj formularz dodawania pasażera na podmianę
        // this.swappedPassenger = this.initializeNewPassenger();

        // Opcjonalnie: Zamknij sekcję podpowiedzi
        // this.showSwappedPassengerSuggestions = false;
    },
    removePassenger(index) {
      // Usuń pasażera z listy
      this.localOrder.passenger.splice(index, 1);
    },
    closeModal() {
      this.$emit('close');
    },
    async submitForm() {
      if (this.isSubmitting || !this.localOrder) return;

      this.isSubmitting = true;
      this.validateField('mpk');
      this.validateField('companyId');
      // this.validateField('passengerId');
      // this.validateField('driverId');
      this.validateField('orderStatus');

      // if (!Object.values(this.localErrors).some(error => error !== '')) {
      this.$emit('submit', this.localOrder);
      // }

      this.isSubmitting = false;
    },
    filterAddressSuggestions(passenger, type) {
      let query = '';
      let addresses = [];
      let allSuggestions = [];

      if (!Array.isArray(this.stations)) {
        console.error('Stacje nie są tablicą:', this.stations);
        return;
      }

      if (!Array.isArray(this.hotels)) {
        console.error('Hotele nie są tablicą:', this.hotels);
        return;
      }

      const fullPassenger = this.passengers.find(p => p.id === passenger.passengerId) || {
        homeAddress: '',
        workAddress: ''
      };

      if (!Array.isArray(passenger.filteredPickupAddresses)) {
        passenger.filteredPickupAddresses = [];
      }

      if (type === 'pickup') {
        query = this.removeDiacritics(passenger.pickupAddress);
        addresses = [fullPassenger.city + " " + fullPassenger.homeAddress, fullPassenger.workAddress].filter(addr => addr);
        
        const filteredAddresses = addresses.filter(address => 
          this.removeDiacritics(address).includes(query)
        );
        
        const filteredStations = this.stations.filter(station => 
          this.removeDiacritics(station.name).includes(query)
        );
        
        const filteredHotels = this.hotels.filter(hotel => 
          this.removeDiacritics(hotel.name).includes(query) || 
          (hotel.address && this.removeDiacritics(hotel.address).includes(query))
        );
        
        allSuggestions = [
          ...filteredAddresses.map(addr => ({ combined: addr, name: addr, postcode: '' })),
          ...filteredStations.map(station => ({ 
            combined: `${station.name} ${station.postcode || 'Brak kodu'}`,
            name: station.name, 
            postcode: station.postcode || 'Brak kodu' 
          })),
          ...filteredHotels.map(hotel => ({
            combined: `${hotel.address || 'Brak adresu'} (${hotel.name})`, 
            name: hotel.name,
            address: hotel.address || 'Brak adresu'
          }))
        ];
        passenger.filteredPickupAddresses = allSuggestions;
      } else if (type === 'dropoff') {
        query = this.removeDiacritics(passenger.dropoffAddress);
        addresses = [fullPassenger.city + " " + fullPassenger.homeAddress, fullPassenger.workAddress].filter(addr => addr);
        
        const filteredAddresses = addresses.filter(address => 
          this.removeDiacritics(address).includes(query)
        );
        
        const filteredStations = this.stations.filter(station => 
          this.removeDiacritics(station.name).includes(query)
        );
        
        const filteredHotels = this.hotels.filter(hotel => 
          this.removeDiacritics(hotel.name).includes(query) || 
          (hotel.address && this.removeDiacritics(hotel.address).includes(query))
        );
        
        allSuggestions = [
          ...filteredAddresses.map(addr => ({ combined: addr, name: addr, postcode: '' })),
          ...filteredStations.map(station => ({ 
            combined: `${station.name} ${station.postcode || 'Brak kodu'}`, 
            name: station.name, 
            postcode: station.postcode || 'Brak kodu' 
          })),
          ...filteredHotels.map(hotel => ({
            combined: `${hotel.address || 'Brak adresu'} (${hotel.name})`, 
            name: hotel.name,
            address: hotel.address || 'Brak adresu'
          }))
        ];
        passenger.filteredDropoffAddresses = allSuggestions;
      }
    },
    filterNewAddressSuggestions(passenger, type) {
      let query = '';
      let addresses = [];
      let allSuggestions = [];

      if (!Array.isArray(this.stations)) {
        console.error('Stacje nie są tablicą:', this.stations);
        return;
      }

      if (!Array.isArray(this.hotels)) {
        console.error('Hotele nie są tablicą:', this.hotels);
        return;
      }

      const fullPassenger = this.passengers.find(p => p.id === passenger.passengerId) || {
        homeAddress: '',
        workAddress: ''
      };

      if (!Array.isArray(passenger.filteredPickupAddresses)) {
        passenger.filteredPickupAddresses = [];
      }

      if (type === 'pickup') {
        query = this.removeDiacritics(passenger.pickupAddress);
        addresses = [fullPassenger.city + " " + fullPassenger.homeAddress, fullPassenger.workAddress].filter(addr => addr);
        
        const filteredAddresses = addresses.filter(address => 
          this.removeDiacritics(address).includes(query)
        );
        
        const filteredStations = this.stations.filter(station => 
          this.removeDiacritics(station.name).includes(query)
        );
        
        const filteredHotels = this.hotels.filter(hotel => 
          this.removeDiacritics(hotel.name).includes(query) || 
          (hotel.address && this.removeDiacritics(hotel.address).includes(query))
        );

        allSuggestions = [
          ...filteredAddresses.map(addr => ({ combined: addr, name: addr, postcode: '' })),
          ...filteredStations.map(station => ({ 
            combined: `${station.name} ${station.postcode || 'Brak kodu'}`, 
            name: station.name, 
            postcode: station.postcode || 'Brak kodu' 
          })),
          ...filteredHotels.map(hotel => ({
            combined: `${hotel.address || 'Brak adresu'} (${hotel.name})`, 
            name: hotel.name,
            address: hotel.address || 'Brak adresu'
          }))
        ];
        passenger.filteredPickupAddresses = allSuggestions;
      } else if (type === 'dropoff') {
        query = this.removeDiacritics(passenger.dropoffAddress);
        addresses = [fullPassenger.city + " " + fullPassenger.homeAddress, fullPassenger.workAddress].filter(addr => addr);
        
        const filteredAddresses = addresses.filter(address => 
          this.removeDiacritics(address).includes(query)
        );
        
        const filteredStations = this.stations.filter(station => 
          this.removeDiacritics(station.name).includes(query)
        );
        
        const filteredHotels = this.hotels.filter(hotel => 
          this.removeDiacritics(hotel.name).includes(query) || 
          (hotel.address && this.removeDiacritics(hotel.address).includes(query))
        );
        allSuggestions = [
          ...filteredAddresses.map(addr => ({ combined: addr, name: addr, postcode: '' })),
          ...filteredStations.map(station => ({ 
            combined: `${station.name} ${station.postcode || 'Brak kodu'}`, 
            name: station.name, 
            postcode: station.postcode || 'Brak kodu' 
          })),
          ...filteredHotels.map(hotel => ({
            combined: `${hotel.address || 'Brak adresu'} (${hotel.name})`,
            name: hotel.name,
            address: hotel.address || 'Brak adresu'
          }))
        ];
        passenger.filteredDropoffAddresses = allSuggestions;
      }
    },
    selectPickupAddress(address, passenger) {
      const finalAddress = address.address || address.combined;
      passenger.pickupAddress = finalAddress;
      this.showPickupSuggestions = false; // Ukryj listę podpowiedzi
    },
    selectDropoffAddress(address, passenger) {
      const finalAddress = address.address || address.combined;
      passenger.dropoffAddress = finalAddress;
      this.showDropoffSuggestions = false; // Ukryj listę podpowiedzi
    },
    hidePickupSuggestions() {
      setTimeout(() => {
        this.showPickupSuggestions = false;
      }, 100);
    },
    hideDropoffSuggestions() {
      setTimeout(() => {
        this.showDropoffSuggestions = false;
      }, 100);
    },
    async getPassengerById(id) {
      try {
        const passengerData = await passengersService.getPassengerById(id);
        return passengerData.data;
      } catch (error) {
        console.error(error)
      }
    },
    filterPassengers() {
        // 1. Oczyszczenie i normalizacja zapytania użytkownika
        const cleanedQuery = this.removeDiacritics(this.currentPassenger.passengerName);

        if (cleanedQuery) {
            this.filteredPassengers = this.passengers
                .filter(passenger => {
                    
                    // 2. Utworzenie i oczyszczenie pełnego imienia i nazwiska pasażera
                    const passengerFullName = `${passenger.lastName} ${passenger.firstName}`;
                    const cleanedPassengerName = this.removeDiacritics(passengerFullName);

                    // 3. Wyszukiwanie (Case- i Diacritic-Insensitive)
                    return cleanedPassengerName.includes(cleanedQuery);
                })
                // Opcjonalne: sortowanie wyników, np. alfabetycznie po nazwisku
                // .sort((a, b) => a.lastName.localeCompare(b.lastName)) 
                
                // 4. Ograniczenie liczby wyników do 3
                .slice(0, 3);
                
            this.showPassengerSuggestions = this.filteredPassengers.length > 0;
        } else {
            // Gdy pole wyszukiwania jest puste
            this.filteredPassengers = [];
            this.showPassengerSuggestions = false;
        }
    },
    removeDiacritics(text) {
      if (typeof text !== 'string' || !text) return '';
      
      // Normalizacja, usunięcie diakrytyków i specjalna obsługa dla 'ł'
      return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ł/g, 'l'); 
    },
  selectPassenger(passenger) {
    this.currentPassenger.passengerName = `${passenger.lastName} ${passenger.firstName}`;
    this.currentPassenger.firstName = passenger.firstName;
    this.currentPassenger.lastName = passenger.lastName;
    this.currentPassenger.passengerId = passenger.id;
    this.currentPassenger.homeAddress = passenger.homeAddress;
    this.currentPassenger.workAddress = passenger.workAddress;
    this.currentPassenger.city = passenger.city;
    this.currentPassenger.sortOrder = passenger.sortOrder;
    this.currentPassenger.sharedDropoff = passenger.sharedDropoff;
    this.showPassengerSuggestions = false;
  },
  hidePassengerSuggestions() {
    setTimeout(() => {
      this.showPassengerSuggestions = false;
    }, 200);
  },
  setPickupAddressForPassenger(passenger) {
    console.log(passenger);
      if (passenger.pickupOption === 'home') {
        const passengerId = passenger.passengerId;
        const selectedPassengerData = this.passengers.find(p => p.id === passengerId);
        if (selectedPassengerData) {
          passenger.pickupAddress = selectedPassengerData.city + " " + selectedPassengerData.homeAddress || 'Adres Zamieszkania (brak w bazie)';
        } else {
          passenger.pickupAddress = 'Nie znaleziono adresu domowego';
        }
      } else if (passenger.pickupOption === 'work') {
        passenger.pickupAddress = '';
        this.filterNewAddressSuggestions(passenger, 'pickup');
      } else if (passenger.pickupOption === 'manual') {
        passenger.pickupAddress = '';
        this.filterNewAddressSuggestions(passenger, 'pickup');
      }
  },
  setDropoffAddressForPassenger(passenger) {
    if (passenger.dropoffOption === 'home') {
      const passengerId = passenger.passengerId;
      const selectedPassengerData = this.passengers.find(p => p.id === passengerId);
      if (selectedPassengerData) {
        passenger.dropoffAddress = selectedPassengerData.city + " " + selectedPassengerData.homeAddress || 'Adres Domu (brak w bazie)';
      } else {
        passenger.dropoffAddress = 'Nie znaleziono adresu domowego';
      }
    } else if (passenger.dropoffOption === 'work') {
      passenger.dropoffAddress = '';
      this.filterNewAddressSuggestions(passenger, 'dropoff');
    } else if (passenger.dropoffOption === 'manual') {
      passenger.dropoffAddress = ''; // Wyczyść pole przy ręcznym wpisywaniu
      this.filterNewAddressSuggestions(passenger, 'dropoff');
    }
  },
  setPickupAddress() {
      if (this.pickupOption === 'home') {
        const passengerId = this.currentPassenger.passengerId;
        const selectedPassengerData = this.passengers.find(p => p.id === passengerId);
        if (selectedPassengerData) {
          this.currentPassenger.pickupAddress = selectedPassengerData.city + " " + selectedPassengerData.homeAddress || 'Adres Zamieszkania (brak w bazie)';
        } else {
          this.currentPassenger.pickupAddress = 'Nie znaleziono adresu domowego';
        }
      } else if (this.pickupOption === 'work') {
        this.currentPassenger.pickupAddress = '';
        this.filterNewAddressSuggestions(this.currentPassenger, 'pickup');
      } else if (this.pickupOption === 'manual') {
        this.currentPassenger.pickupAddress = '';
        this.filterNewAddressSuggestions(this.currentPassenger, 'pickup');
      }
    },
    setDropoffAddress() {
      if (this.dropoffOption === 'home') {
        const passengerId = this.currentPassenger.passengerId;
        const selectedPassengerData = this.passengers.find(p => p.id === passengerId);
        if (selectedPassengerData) {
          this.currentPassenger.dropoffAddress = selectedPassengerData.city + " " + selectedPassengerData.homeAddress || 'Adres Domu (brak w bazie)';
        } else {
          this.currentPassenger.dropoffAddress = 'Nie znaleziono adresu domowego';
        }
      } else if (this.dropoffOption === 'work') {
        this.currentPassenger.dropoffAddress = '';
        this.filterNewAddressSuggestions(this.currentPassenger, 'dropoff');
      } else if (this.dropoffOption === 'manual') {
        this.currentPassenger.dropoffAddress = '';
        this.filterNewAddressSuggestions(this.currentPassenger, 'dropoff');
      }
    },
  addPassenger() {
    if (!Array.isArray(this.localOrder.passenger)) {
      this.localOrder.passenger = [];
    }
    this.localOrder.passenger.push({ ...this.currentPassenger, isEditing: false });
    this.resetCurrentPassenger();
  },
  showTimePicker(event) {
      event.target.showPicker();
  },
  resetCurrentPassenger() {
    this.currentPassenger = {
      passengerName: '',
      firstName: '',
      lastName: '',
      passengerId: null,
      sortOrder: null,
      sharedDropoff: false,
      pickupTime: '',
      pickupAddress: '',
      dropoffAddress: '',
      homeAddress: '',
      workAddress: '',
      city: ''
    };
    this.pickupOption = '';
    this.dropoffOption = '';
  },
    validateField(field) {
      const order = this.localOrder;
      switch (field) {
        case 'mpk':
          this.localErrors.mpk = order.mpk.length < 2 ? 'MPK musi mieć co najmniej 2 znaki' : '';
          break;
        case 'companyId':
          this.localErrors.companyId = !order.companyId ? 'Firma jest wymagana' : '';
          break;
        case 'orderStatus':
          this.localErrors.orderStatus = !order.orderStatus ? 'Status zamówienia jest wymagany' : '';
          break;
      }
    },
    filterWorkAddresses() {
      this.filteredWorkAddresses = this.stations.map(station => station.name);
    },
    filterDrivers() {
        const query = this.localOrder.driverName.toLowerCase();
        
        // Zacznij od pustej listy
        let filtered = [];
        
        // 1. Sprawdź, czy wpisany tekst jest zbliżony do "brak" lub jest pusty
        if (query.length > 0 && ('brak kierowcy').includes(query) || query === '') {
             // Dodaj opcję 'Brak kierowcy' na początku listy
            filtered.push(this.noDriverOption);
        }

        // 2. Filtruj rzeczywistych kierowców
        const actualDrivers = this.drivers.filter(driver => 
            driver.firstName.toLowerCase().includes(query) || 
            driver.lastName.toLowerCase().includes(query)
        );
        
        // 3. Połącz listy i usuń duplikaty (jeśli nazwa kierowcy koliduje z "Brak kierowcy")
        this.filteredDrivers = [...filtered, ...actualDrivers];
    },
    selectDriver(driver) {
      this.localOrder.driverId = driver.id;
      this.localOrder.driverName = `${driver.firstName} ${driver.lastName}`;
      this.showDriverSuggestions = false;
    },
    hideDriverSuggestions() {
      setTimeout(() => {
        this.showDriverSuggestions = false;
      }, 200);
    },
    filterSwappedPassengers() {
        // 1. Oczyszczenie i normalizacja zapytania użytkownika (Diacritic-Insensitive)
        const rawQuery = this.swappedPassenger.passengerName;
        const cleanedQuery = this.removeDiacritics(rawQuery).trim();

        if (cleanedQuery) {
            this.filteredSwappedPassengers = this.passengers
                .filter(passenger => {
                    
                    // 2. Oczyszczenie imienia i nazwiska pasażera
                    const cleanedFirstName = this.removeDiacritics(passenger.firstName);
                    const cleanedLastName = this.removeDiacritics(passenger.lastName);
                    
                    // 3. Sprawdzenie, czy to nie jest pasażer obecnie edytowany
                    const isNotCurrentPassenger = passenger.id !== this.currentPassenger.passengerId;

                    // 4. Wyszukiwanie na oczyszczonych ciągach znaków
                    const firstNameMatches = cleanedFirstName.includes(cleanedQuery);
                    const lastNameMatches = cleanedLastName.includes(cleanedQuery);

                    return (firstNameMatches || lastNameMatches) && isNotCurrentPassenger;
                })
                // 5. Sortowanie i ograniczenie wyników (zachowane z oryginalnej logiki)
                .sort((a, b) => a.lastName.localeCompare(b.lastName))
                .slice(0, 3); 

            this.showSwappedPassengerSuggestions = this.filteredSwappedPassengers.length > 0;
        } else {
            // Kiedy pole jest puste, pokazujemy wszystkich pasażerów oprócz aktualnego (bez zmian)
            this.filteredSwappedPassengers = this.passengers.filter(p => p.id !== this.currentPassenger.passengerId);
            this.showSwappedPassengerSuggestions = this.filteredSwappedPassengers.length > 0;
        }
    },
    selectSwappedPassenger(passengerFix, index) {
      this.swappedPassenger.firstName = passengerFix.firstName;
      this.swappedPassenger.lastName = passengerFix.lastName;
      this.swappedPassenger.passengerName = `${passengerFix.lastName} ${passengerFix.firstName}`;
      this.swappedPassenger.passengerId = passengerFix.id;
      this.swappedPassenger.city = passengerFix.city;
      // Adres odbioru drugiego pasażera jest adresem dowozu pierwszego
      this.swappedPassenger.homeAddress = passengerFix.homeAddress;
      this.swappedPassenger.pickupAddress = this.localOrder.passenger[index].dropoffAddress;
      this.swappedPassenger.swapped = true;
      this.swappedPassenger.swappedPassengerId = this.localOrder.passenger[index].passengerId,
      this.showSwappedPassengerSuggestions = false;
    },
    hideSwappedPassengerSuggestions() {
        setTimeout(() => {
          this.showSwappedPassengerSuggestions = false;
        }, 100);
      },
    filterSwappedDropoffAddresses() {
      const query = this.removeDiacritics(this.swappedPassenger.dropoffAddress);

      let allSuggestions = [];

      const homeAddress = this.removeDiacritics(this.swappedPassenger.city + " " + this.swappedPassenger.homeAddress);
      const filteredHotels = this.hotels.filter(hotel => 
        this.removeDiacritics(hotel.name).includes(query) || 
        (hotel.address && this.removeDiacritics(hotel.address).includes(query))
      );

      allSuggestions = [
        {
          combined: homeAddress,
          name: homeAddress,
          postcode: ''
        },
        ...filteredHotels.map(hotel => ({
          combined: `${hotel.address || 'Brak adresu'} (${hotel.name})`, 
          name: hotel.name,
          address: hotel.address || 'Brak adresu'
        }))
      ];

      this.filteredSwappedDropoffAddresses = allSuggestions;

      if (!query) {
        return;
      }

      if (query.length > 6) {
        this.swapPassengerSelected = true;
      }

      // const filteredStations = this.stations.filter(station =>
      //   station.name.toLowerCase().includes(query)
      // );

      // this.filteredSwappedDropoffAddresses = filteredStations.map(station => ({
      //   combined: `${station.name} ${station.postcode || 'Brak kodu'}`,
      //   name: station.name,
      //   postcode: station.postcode || 'Brak kodu'
      // }));

      this.filteredSwappedDropoffAddresses = allSuggestions;
    },
    selectSwappedDropoffAddress(address) {
      const finalAddress = address.address || address.combined;
      this.swappedPassenger.dropoffAddress = finalAddress;
      this.showSwappedDropoffSuggestions = false;
      this.swapPassengerSelected = true;
    },
    hideSwappedDropoffSuggestions() {
      setTimeout(() => {
        this.showSwappedDropoffSuggestions = false;
        // this.checkSwappedAndAddPassenger();
      }, 100);
    },
    checkSwappedAndAddPassenger() {
      console.log('checkAndAddPassenger wywołane', {
        passengerName: this.currentPassenger.passengerName,
        passengerId: this.currentPassenger.passengerId,
        pickupTime: this.currentPassenger.pickupTime,
        pickupAddress: this.currentPassenger.pickupAddress,
        dropoffAddress: this.currentPassenger.dropoffAddress
      });
      if (this.swapEnabled && this.swappedPassenger.passengerId) {
          this.swappedPassenger.pickupTime = this.currentPassenger.pickupTime;
          this.swappedPassenger.passengerName = this.swappedPassenger.passengerName || `${this.currentPassenger.lastName} ${this.currentPassenger.firstName}`;
          this.swappedPassenger.pickupAddress = this.currentPassenger.dropoffAddress;
          this.localOrder.passenger.push({ ...this.swappedPassenger });
      }
    },
    startDrag(event) {
        this.isDragging = true;
        
        event.currentTarget.classList.add('dragging');

        const clientX = event.clientX || event.touches[0].clientX;
        const clientY = event.clientY || event.touches[0].clientY;
        
        this.dragOffset.x = clientX - this.modalPosition.x;
        this.dragOffset.y = clientY - this.modalPosition.y;
        
        document.addEventListener('mousemove', this.onDrag);
        document.addEventListener('touchmove', this.onDrag, { passive: false });
        document.addEventListener('mouseup', this.stopDrag);
        document.addEventListener('touchend', this.stopDrag);
      },
    onDrag(event) {
        if (!this.isDragging) return;
        
        event.preventDefault();
        
        const clientX = event.clientX || event.touches[0].clientX;
        const clientY = event.clientY || event.touches[0].clientY;
        
        this.modalPosition.x = clientX - this.dragOffset.x;
        this.modalPosition.y = clientY - this.dragOffset.y;
      },
    stopDrag() {
        this.isDragging = false;
        
        const dragElement = document.querySelector('.cursor-move');
        if (dragElement) {
          dragElement.classList.remove('dragging');
        }

        // Usuwamy event listeners
        document.removeEventListener('mousemove', this.onDrag);
        document.removeEventListener('touchmove', this.onDrag);
        document.removeEventListener('mouseup', this.stopDrag);
        document.removeEventListener('touchend', this.stopDrag);
      },
    resetPosition() {
        this.modalPosition = { x: 0, y: 0 };
    },
  }
};
</script>
